import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@deepguide-ai/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@deepguide-ai/ui/command'
import { Check, ChevronDown } from 'lucide-react'
import { UserAvatar } from '@deepguide-ai/ui/user-avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { AvatarGroup } from '@/components/compliance/AvatarGroup'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

export const ROLE_NONE_VALUE = '__none__'

export type RoleAssignmentStatus =
  | 'can_act'
  | 'auto_upgraded'
  | 'unlinked'
  | 'blocked_persona'

export type RoleHolder = {
  id?: string | null
  firstName?: string | null
  lastName?: string | null
  name?: string | null
  email?: string | null
  image?: string | null
  assignmentStatus?: RoleAssignmentStatus
}

export type RoleSelectRole = {
  id: string
  name: string
  holders?: readonly RoleHolder[]
}

function holderDisplayName(holder: RoleHolder): string | null {
  const fullName = [holder.firstName, holder.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  return fullName || holder.name || holder.email || null
}

export function normalizeRoleSelectValue(value: string): string | null {
  return value === ROLE_NONE_VALUE ? null : value
}

/** Return the earliest holder's display name for compact single-name surfaces. */
export function roleHolderName(role: RoleSelectRole): string | null {
  return holderDisplayName(role.holders?.[0] ?? {})
}

function assignmentStatusLabel(status?: RoleAssignmentStatus): string | null {
  if (status === 'auto_upgraded') return 'Auto-upgrade'
  if (status === 'blocked_persona') return 'Unavailable'
  return null
}

function RoleHolderAvatars({
  holders,
  className,
}: {
  holders: readonly RoleHolder[]
  className?: string
}) {
  if (holders.length === 0) return null

  return (
    <AvatarGroup
      users={holders.map((holder, index) => {
        const name = holderDisplayName(holder) ?? 'Unassigned'
        return {
          id: holder.id ?? `${name}-${index}`,
          name,
          email: holder.email ?? undefined,
          image: holder.image ?? null,
        }
      })}
      max={3}
      size='sm'
      showIndividualTooltips={false}
      className={className}
    />
  )
}

function RoleHolderTooltip({
  holders,
  children,
}: {
  holders: readonly RoleHolder[]
  children: ReactNode
}) {
  if (holders.length === 0) return children

  const holderList = (
    <div className='space-y-2'>
      {holders.map((holder, index) => {
        const name = holderDisplayName(holder) ?? 'Unknown'
        const showEmail = holder.email && holder.email !== name
        return (
          <div
            key={holder.id ?? `${name}-${index}`}
            className='flex min-w-0 items-center gap-2'
          >
            <UserAvatar
              name={name}
              email={holder.email}
              image={holder.image}
              size='xs'
              className='shrink-0'
            />
            <div className='min-w-0'>
              <div className='truncate text-xs font-medium text-text-secondary'>
                {name}
              </div>
              {showEmail && (
                <div className='truncate text-[10px] text-text-subtle'>
                  {holder.email}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side='left'
        align='start'
        sideOffset={8}
        className='w-60 rounded-md border border-border bg-popover p-3 text-left text-popover-foreground shadow-md'
      >
        <div className='mb-2 text-[10px] font-semibold tracking-wide text-text-subtle uppercase'>
          {holders.length} {holders.length === 1 ? 'holder' : 'holders'}
        </div>
        {holders.length > 6 ? (
          <ScrollArea className='h-52 pr-3' type='scroll'>
            {holderList}
          </ScrollArea>
        ) : holderList}
      </TooltipContent>
    </Tooltip>
  )
}

export function RoleSelectedLabel({ role }: { role: RoleSelectRole }) {
  return (
    <div className='flex max-w-[220px] min-w-0 items-center gap-1'>
      <span className='max-w-[180px] min-w-0 truncate'>{role.name}</span>
      <RoleHolderAvatars holders={role.holders ?? []} className='ml-0.5' />
    </div>
  )
}

/** Shared role + holder rendering for Select and composite pickers. */
export function RoleOptionLabel({
  role,
  className,
}: {
  role: RoleSelectRole
  className?: string
}) {
  const holders = role.holders ?? []
  const status = holders.find(
    (holder) => holder.assignmentStatus === 'blocked_persona'
  )?.assignmentStatus
  const statusLabel = assignmentStatusLabel(status)
  const visibleHolderNames = holders
    .slice(0, 3)
    .map((holder) => holderDisplayName(holder) ?? 'Unknown')
  const hiddenHolderCount = Math.max(holders.length - visibleHolderNames.length, 0)

  return (
    <div
      className={cn('flex min-w-0 flex-col items-start gap-0.5', className)}
    >
      <span className='max-w-full truncate'>{role.name}</span>
      {(holders.length > 0 || statusLabel) && (
        <div className='text-muted-foreground flex max-w-full min-w-0 items-center gap-1 text-xs leading-4'>
          {holders.length > 0 && (
            <>
              <RoleHolderAvatars holders={holders} />
              <span className='max-w-[14rem] truncate'>
                {visibleHolderNames.join(', ')}
                {hiddenHolderCount > 0 && ` +${hiddenHolderCount} more`}
              </span>
            </>
          )}
          {statusLabel && (
            <span
              className={cn(
                'shrink-0',
                status === 'blocked_persona'
                  ? 'text-status-red'
                  : 'text-status-orange'
              )}
            >
              {statusLabel}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export interface RoleSelectProps {
  value?: string | null
  onValueChange: (roleId: string | null) => void
  /** Pass an existing query result to avoid taking ownership of data loading. */
  roles?: readonly RoleSelectRole[]
  readOnly?: boolean
  disabled?: boolean
  placeholder?: string
  noneLabel?: string
  /** Accessible name for the combobox when no visible label is associated. */
  ariaLabel?: string
  /** ID of a visible label associated with the combobox. */
  ariaLabelledBy?: string
  /** Optional leading icon or label used by compact toolbar pickers. */
  triggerLeading?: ReactNode
  className?: string
  fullWidth?: boolean
}

/**
 * Reusable ComplianceRole selector. It owns only role presentation and the
 * empty-value conversion; callers own persistence and page-specific layout.
 */
export function RoleSelect({
  value,
  onValueChange,
  roles: providedRoles,
  readOnly = false,
  disabled = false,
  placeholder = 'Not assigned',
  noneLabel = 'Not assigned',
  ariaLabel = 'Compliance role',
  ariaLabelledBy,
  triggerLeading,
  fullWidth = false,
  className,
}: RoleSelectProps) {
  const [open, setOpen] = useState(false)
  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
    value ?? null
  )

  useEffect(() => {
    if (value !== undefined) setUncontrolledValue(value)
  }, [value])

  const rolesQuery = useQuery({
    ...trpc.complianceRole.list.queryOptions(),
    enabled: providedRoles === undefined,
  })
  const roles = providedRoles ?? rolesQuery.data ?? []
  const selectedValue = value !== undefined ? value : uncontrolledValue
  const selectedRole = roles.find((role) => role.id === selectedValue) ?? null
  const isLoading = providedRoles === undefined && rolesQuery.isPending
  const hasError = providedRoles === undefined && rolesQuery.isError
  const selectPlaceholder = isLoading
    ? 'Loading roles…'
    : hasError
      ? 'Unable to load roles'
      : placeholder

  if (readOnly) {
    return (
      selectedRole ? (
        <RoleHolderTooltip holders={selectedRole.holders ?? []}>
          <span className={cn('text-text-body block text-sm', className)}>
            <RoleOptionLabel role={selectedRole} />
          </span>
        </RoleHolderTooltip>
      ) : (
        <span className={cn('text-text-body block text-sm', className)}>{noneLabel}</span>
      )
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <RoleHolderTooltip holders={selectedRole?.holders ?? []}>
        <PopoverTrigger asChild>
          <button
            type='button'
            role='combobox'
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            disabled={disabled || isLoading || hasError}
            className={cn(
              'border-input bg-card flex h-9 w-fit cursor-pointer items-center justify-between gap-2 !rounded-sm border px-3 py-2 text-sm whitespace-nowrap shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:shrink-0',
              fullWidth && 'w-full',
              className
            )}
          >
            <span className='flex min-w-0 items-center gap-1'>
              {triggerLeading && (
                <span className='flex shrink-0 items-center'>
                  {triggerLeading}
                </span>
              )}
              {selectedRole ? (
                <RoleSelectedLabel role={selectedRole} />
              ) : (
                <span className='text-muted-foreground truncate'>{selectPlaceholder}</span>
              )}
            </span>
            <ChevronDown className='text-muted-foreground size-4 opacity-50' />
          </button>
        </PopoverTrigger>
      </RoleHolderTooltip>
      <PopoverContent
        align='start'
        className='w-[260px] min-w-[var(--radix-popover-trigger-width)] p-0'
      >
        <Command label='Search roles'>
          <CommandInput placeholder='Search roles...' className='h-8 text-sm' />
          <CommandList>
            {roles.length > 0 && <CommandEmpty>No role found</CommandEmpty>}
            <CommandGroup>
              <CommandItem
                value={ROLE_NONE_VALUE}
                keywords={[noneLabel]}
                onSelect={() => {
                  if (value === undefined) setUncontrolledValue(null)
                  onValueChange(null)
                  setOpen(false)
                }}
                className='text-muted-foreground'
              >
                {noneLabel}
                {!selectedValue && <Check className='ml-auto size-4' />}
              </CommandItem>
              {roles.map((role) => (
                <RoleHolderTooltip key={role.id} holders={role.holders ?? []}>
                  <CommandItem
                    value={role.id}
                    keywords={[
                      role.name,
                      ...(role.holders ?? []).flatMap((holder) => [
                        holderDisplayName(holder) ?? '',
                        holder.email ?? '',
                      ]),
                    ]}
                    onSelect={() => {
                      if (value === undefined) setUncontrolledValue(role.id)
                      onValueChange(role.id)
                      setOpen(false)
                    }}
                  >
                    <RoleOptionLabel role={role} />
                    {selectedValue === role.id && <Check className='ml-auto size-4' />}
                  </CommandItem>
                </RoleHolderTooltip>
              ))}
            </CommandGroup>
            {roles.length === 0 && (
              <div className='font-body text-text-placeholder px-2 py-1.5 text-[11px] leading-snug'>
                No compliance roles yet. Add them in Settings → Compliance roles.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
